/** Local dev-only: simulate roles for UI without fake users. APIs still use the real JWT. */

export const DEV_IMPERSONATION_STORAGE_KEY = "bb_dev_impersonation_v1";
export const DEV_IMPERSONATION_EVENT = "bb-dev-impersonation-changed";

export type DevImpersonationV1 = {
  v: 1;
  enabled: boolean;
  /** Normalized lowercase roles to merge into permission checks (e.g. sales + trainer). */
  roles: string[];
  /**
   * When true, workspace org picker may target any org UUID (set only if saver had platform_dev).
   * Used with `WorkspaceOrgProvider` validation.
   */
  workspaceBrowseAllOrgs: boolean;
  /** Optional contacts trust-band override for testing path-search visibility (1..3). */
  trustLevelOverride: 1 | 2 | 3 | null;
};

const EMPTY: DevImpersonationV1 = {
  v: 1,
  enabled: false,
  roles: [],
  workspaceBrowseAllOrgs: false,
  trustLevelOverride: null,
};

function normalizeRoles(raw: string[]): string[] {
  const s = new Set<string>();
  for (const r of raw) {
    const t = String(r || "")
      .trim()
      .toLowerCase();
    if (t) s.add(t);
  }
  return [...s].sort();
}

export function readDevImpersonation(): DevImpersonationV1 {
  try {
    const raw = localStorage.getItem(DEV_IMPERSONATION_STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const p = JSON.parse(raw) as Partial<DevImpersonationV1>;
    if (p?.v !== 1) return { ...EMPTY };
    return {
      v: 1,
      enabled: !!p.enabled && normalizeRoles(p.roles ?? []).length > 0,
      roles: normalizeRoles(p.roles ?? []),
      workspaceBrowseAllOrgs: !!p.workspaceBrowseAllOrgs,
      trustLevelOverride:
        Number(p.trustLevelOverride) === 1 || Number(p.trustLevelOverride) === 2 || Number(p.trustLevelOverride) === 3
          ? (Number(p.trustLevelOverride) as 1 | 2 | 3)
          : null,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function writeDevImpersonation(state: DevImpersonationV1) {
  const next: DevImpersonationV1 = {
    v: 1,
    enabled: !!state.enabled && state.roles.length > 0,
    roles: normalizeRoles(state.roles),
    workspaceBrowseAllOrgs: !!state.workspaceBrowseAllOrgs && !!state.enabled && state.roles.length > 0,
    trustLevelOverride:
      state.trustLevelOverride === 1 || state.trustLevelOverride === 2 || state.trustLevelOverride === 3
        ? state.trustLevelOverride
        : null,
  };
  try {
    if (!next.enabled) {
      localStorage.removeItem(DEV_IMPERSONATION_STORAGE_KEY);
    } else {
      localStorage.setItem(DEV_IMPERSONATION_STORAGE_KEY, JSON.stringify(next));
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(DEV_IMPERSONATION_EVENT));
}

export function clearDevImpersonation() {
  writeDevImpersonation({ ...EMPTY, enabled: false });
}

export function isUuidLike(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id.trim());
}
